import {
  TextField,
  Paper,
  Slider,
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'

import RamSlider from './RamSlider'

import { Provider } from '../types/Server'
import ServerFilter from '../types/ServerFilter'
import { DiskSizeOptions, ProviderLocations, countActiveFilters } from '../utils/filtering'
import { formatDiskSize, PROVIDER_NAMES } from '../utils/servers'

const LOCATION_NAMES: Record<string, string> = {
  FSN1: 'Falkenstein',
  HEL1: 'Helsinki',
  NBG1: 'Nuremberg',
}

type DiskFilterSectionProps = {
  title: string
  countRange: [number, number]
  maxCount: number
  sizeRange: [number, number]
  sizes: number[]
  onCountChange: (min: number, max: number) => void
  onSizeChange: (min: number, max: number) => void
}

const DiskFilterSection: React.FC<DiskFilterSectionProps> = ({
  title,
  countRange,
  maxCount,
  sizeRange,
  sizes,
  onCountChange,
  onSizeChange,
}) => {
  if (maxCount === 0) return null

  const maxSize = sizes[sizes.length - 1] ?? 0
  const sizeDisabled = countRange[0] === 0
  // step=null restricts the thumbs to the mark values, i.e. sizes that actually exist
  const sizeMarks = [
    { value: 0, label: '0' },
    ...sizes.map((size) => ({
      value: size,
      label: size === maxSize ? formatDiskSize(size) : undefined,
    })),
  ]

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
        {title}
      </Typography>

      <Typography variant="body2" fontWeight="medium">
        Disk Count
      </Typography>
      <Slider
        value={countRange}
        min={0}
        max={maxCount}
        step={1}
        valueLabelDisplay="auto"
        onChange={(_e, value) => {
          if (Array.isArray(value) && value.length === 2) {
            onCountChange(value[0], value[1])
          }
        }}
        marks={[
          { value: 0, label: '0' },
          { value: maxCount, label: `${maxCount}` },
        ]}
      />

      <Typography
        variant="body2"
        fontWeight="medium"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
      >
        <span>Disk Size</span>
        {sizeDisabled && (
          <Typography component="span" variant="caption" color="text.disabled">
            set a min count to enable
          </Typography>
        )}
      </Typography>
      <Slider
        disabled={sizeDisabled}
        value={sizeRange}
        min={0}
        max={maxSize}
        step={null}
        getAriaValueText={formatDiskSize}
        valueLabelFormat={formatDiskSize}
        onChange={(_e, value) => {
          if (Array.isArray(value) && value.length === 2) {
            onSizeChange(value[0], value[1])
          }
        }}
        valueLabelDisplay="auto"
        marks={sizeMarks}
      />
    </Box>
  )
}

type FilterBarProps = {
  filters: ServerFilter
  initialFilters: ServerFilter
  setFilters: (filters: ServerFilter) => void
  locations: ProviderLocations[]
  diskSizes: DiskSizeOptions
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  initialFilters,
  setFilters,
  locations,
  diskSizes,
}) => {
  const activeCount = countActiveFilters(filters, initialFilters)
  const showNvme = initialFilters.maxNvmeCount > 0
  const showSata = initialFilters.maxSataCount > 0
  const showHdd = initialFilters.maxHddCount > 0
  const multiProvider = initialFilters.providers.length > 1
  const visibleLocations = locations.filter(
    (group) => filters.providers.includes(group.provider) && group.locations.length > 1
  )

  const toggleLocation = (location: string) =>
    setFilters({
      ...filters,
      locations: filters.locations.includes(location)
        ? filters.locations.filter((l) => l !== location)
        : [...filters.locations, location],
    })

  // at least one provider stays selected; locations of a deselected one are dropped
  const setProviders = (providers: Provider[]) => {
    if (providers.length === 0) return
    const stillVisible = new Set(
      locations.filter((g) => providers.includes(g.provider)).flatMap((g) => g.locations)
    )
    setFilters({
      ...filters,
      providers,
      locations: filters.locations.filter((location) => stillVisible.has(location)),
    })
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={0}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <Button size="small" disabled={activeCount === 0} onClick={() => setFilters(initialFilters)}>
          Reset
        </Button>
      </Stack>

      {multiProvider && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Provider
          </Typography>
          <ToggleButtonGroup
            value={filters.providers}
            onChange={(_e, value: Provider[]) => setProviders(value)}
            size="small"
            fullWidth
          >
            {initialFilters.providers.map((provider) => (
              <ToggleButton key={provider} value={provider} sx={{ textTransform: 'none' }}>
                {PROVIDER_NAMES[provider]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <TextField
        label="CPU"
        fullWidth
        size="small"
        value={filters.cpu}
        onChange={(e) => setFilters({ ...filters, cpu: e.target.value })}
      />

      {visibleLocations.map(({ provider, locations: providerLocations }) => (
        <Box key={provider} sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Location{multiProvider && ` · ${PROVIDER_NAMES[provider]}`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {providerLocations.map((location) => (
              <ToggleButton
                key={location}
                value={location}
                size="small"
                selected={filters.locations.includes(location)}
                onChange={() => toggleLocation(location)}
                sx={{ textTransform: 'none', flex: '1 1 auto' }}
              >
                {LOCATION_NAMES[location] ?? location}
              </ToggleButton>
            ))}
          </Box>
        </Box>
      ))}

      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Switch
            checked={filters.eccOnly}
            onChange={(e) => setFilters({ ...filters, eccOnly: e.target.checked })}
          />
        }
        label="ECC RAM only"
      />

      <Box sx={{ mt: 1 }}>
        <Typography gutterBottom>Max price (€/month incl. VAT & IPv4)</Typography>
        <Slider
          value={filters.maxPrice}
          min={0}
          step={1}
          max={initialFilters.maxPrice}
          getAriaValueText={(v) => `${v} €`}
          valueLabelFormat={(v) => `${v} €`}
          onChange={(_e, value) => setFilters({ ...filters, maxPrice: value as number })}
          valueLabelDisplay="auto"
          marks={[
            { value: 0, label: '0 €' },
            { value: initialFilters.maxPrice, label: `${initialFilters.maxPrice} €` },
          ]}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <RamSlider
          value={[filters.minRAM, filters.maxRAM]}
          maxRAM={initialFilters.maxRAM}
          setValue={(value) => setFilters({ ...filters, minRAM: value[0], maxRAM: value[1] })}
        />
      </Box>

      <Box sx={{ mt: 2, mb: 2 }}>
        <Divider>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            DISK FILTERS
          </Typography>
        </Divider>
      </Box>

      <DiskFilterSection
        title="NVMe Disks"
        countRange={[filters.minNvmeCount, filters.maxNvmeCount]}
        maxCount={initialFilters.maxNvmeCount}
        sizeRange={[filters.minNvmeSize, filters.maxNvmeSize]}
        sizes={diskSizes.nvme}
        onCountChange={(min, max) =>
          setFilters({
            ...filters,
            minNvmeCount: min,
            maxNvmeCount: max,
            // the size window only acts while a min count is set — clear it on disable
            ...(min === 0 && { minNvmeSize: 0, maxNvmeSize: initialFilters.maxNvmeSize }),
          })
        }
        onSizeChange={(min, max) => setFilters({ ...filters, minNvmeSize: min, maxNvmeSize: max })}
      />

      {showNvme && showSata && (
        <Divider sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight="bold" color="primary">
            OR
          </Typography>
        </Divider>
      )}

      <DiskFilterSection
        title="SATA Disks"
        countRange={[filters.minSataCount, filters.maxSataCount]}
        maxCount={initialFilters.maxSataCount}
        sizeRange={[filters.minSataSize, filters.maxSataSize]}
        sizes={diskSizes.sata}
        onCountChange={(min, max) =>
          setFilters({
            ...filters,
            minSataCount: min,
            maxSataCount: max,
            ...(min === 0 && { minSataSize: 0, maxSataSize: initialFilters.maxSataSize }),
          })
        }
        onSizeChange={(min, max) => setFilters({ ...filters, minSataSize: min, maxSataSize: max })}
      />

      {(showNvme || showSata) && showHdd && (
        <Divider sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight="bold" color="primary">
            AND
          </Typography>
        </Divider>
      )}

      <DiskFilterSection
        title="HDD Disks"
        countRange={[filters.minHddCount, filters.maxHddCount]}
        maxCount={initialFilters.maxHddCount}
        sizeRange={[filters.minHddSize, filters.maxHddSize]}
        sizes={diskSizes.hdd}
        onCountChange={(min, max) =>
          setFilters({
            ...filters,
            minHddCount: min,
            maxHddCount: max,
            ...(min === 0 && { minHddSize: 0, maxHddSize: initialFilters.maxHddSize }),
          })
        }
        onSizeChange={(min, max) => setFilters({ ...filters, minHddSize: min, maxHddSize: max })}
      />
    </Paper>
  )
}

export default FilterBar
